import {makeAutoObservable, runInAction, when} from 'mobx';

import type {DateTimeObj} from '../datetime';
import type {RpcError} from '../errors/api/types';
import type {Destructible, WithReactionDispose} from '../store';
import type {Nullable} from '../utility-types';
import type {CoreOrB2bEnv} from './types';

import {authStore, type StoredSession} from '../../api/auth';
import {currentUserStore, type Role} from '../../api/user';
import {getExcludedEvents} from '../../modules/notifications/stores/Notifications/utils';
import {appStateManager, type AppStateManager} from '../appState';
import {not} from '../conditionalUtils';
import {datetimeObjToISOString} from '../datetime';
import {isUserHasRole} from '../entity/user';
import {envManager} from '../env';
import {
  ErrorJwtExpired,
  ErrorResourceUserExpiredSession,
  parseRpcErrorFactory,
  RpcRequestError,
} from '../errors/api';
import {logger} from '../logger';
import {platform} from '../platform';
import {reloadApp} from '../reloadApp';
import {storage} from '../storage';
import {ReactionDisposeManager} from '../store';
import {CoreOrB2bEnvSwitcherCoreOnly} from './CoreOrB2bEnvSwitcherCoreOnly';

const CORE_COACH_ACCESS_TO_B2B_ROLE: Role = 'approved_personal_coach';

const STORAGE_KEY = 'cb2b_env';

const REFRESH_UPDATES_COUNT_INTERVAL = 1000 * 60 * 5; // 5 minutes

/**
 * @deprecated for now
 */
class CoreOrB2bEnvSwitcher implements Destructible, WithReactionDispose {
  disposer = new ReactionDisposeManager();
  isReady = false;
  private refreshUpdatesTimer: Nullable<number> = null;
  private unsubscribeAppStateChange: () => void;
  // for now we don't need counters but we can use it in future
  private updatesCount = {
    b2b: 0,
    core: 0,
  };

  constructor(private env: CoreOrB2bEnv = 'core') {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });

    this.unsubscribeAppStateChange = appStateManager.onStateChange(
      this.onAppStateChange.bind(this),
    );

    this.setupReactions();

    this.init(env);
  }

  private async init(initialEnv: CoreOrB2bEnv) {
    const storageEnv = (await storage.getItem(STORAGE_KEY)) as CoreOrB2bEnv;

    const hasStorageEnv = Boolean(storageEnv);
    const noStorageEnv = not(hasStorageEnv);

    switch (true) {
      case noStorageEnv:
        await this.setEnv(initialEnv);
        this.setIsReady();
        break;
      case hasStorageEnv:
        this.env = storageEnv;
        this.setIsReady();
        break;
      default:
        throw new Error('CoreOrB2bEnvSwitcher init unexpected case');
    }
  }

  destruct() {
    this.disposer.dispose();

    this.stopFetchingUpdates();

    this.unsubscribeAppStateChange();
  }

  get isB2b() {
    return this.env === 'b2b';
  }

  get isCore() {
    return this.env === 'core';
  }

  get isOppositeEnvHasUpdates() {
    return this.updatesCount[this.oppositeEnv] > 0;
  }

  get isSwitchAvailable() {
    if (!this.isReady) {
      return false;
    }

    // if user is b2b coach he definitely should have core account
    if (this.isB2b) {
      return true;
    }

    return this.isCoreUserHasAccessToB2b();
  }

  get oppositeEnv() {
    return this.env === 'core' ? 'b2b' : 'core';
  }

  get shouldShowButtonTemporary() {
    return true;
  }

  toggleEnv() {
    if (this.isCore) {
      this.switchToB2b();
    } else {
      this.switchToCore();
    }
  }

  private async fetchUpdatesForEnv(env: CoreOrB2bEnv) {
    try {
      const sessionToken = await this.getSessionTokenForEnv(this.oppositeEnv);

      const apiEndpoint =
        envManager.get(env === 'core' ? 'b2bApiEndpoint' : 'apiEndpoint') || '';
      const apiVersion =
        envManager.get(env === 'core' ? 'b2bApiVersion' : 'apiVersion') || '';

      const shouldFetchUpdates = Boolean(
        sessionToken && apiEndpoint && apiVersion,
      );

      if (shouldFetchUpdates) {
        const accessToken = await this.getAccessToken(
          sessionToken,
          apiEndpoint,
          apiVersion,
        );

        if (accessToken) {
          const unreadEventsCount = await this.getUnreadEventsCount(
            this.oppositeEnv,
            accessToken,
            apiEndpoint,
            apiVersion,
          );

          runInAction(() => {
            this.updatesCount[this.oppositeEnv] = unreadEventsCount;
          });
        }
      }
    } catch (error) {
      if (error instanceof RpcRequestError) {
        logger.rpcError(error);
      } else {
        logger.error(error);
      }
    }
  }

  private async getAccessToken(
    sessionToken: string,
    apiEndpoint: string,
    apiVersion: string,
  ) {
    const result = await fetch(apiEndpoint, {
      body: JSON.stringify({
        id: 1,
        meta: {
          version: apiVersion.split('.').map(x => +x),
        },
        method: 'user.auth.refresh',
        params: {
          session_token: sessionToken,
        },
      }),
      method: 'POST',
    });

    const json = (await result.json()) as {
      error: Nullable<RpcError>;
      result: Nullable<{
        access: {token: string};
        session: {_id: string; expires: DateTimeObj; token: string};
      }>;
    };

    if (json.error) {
      const rpcError = parseRpcErrorFactory(json.error, {});

      if (
        rpcError instanceof ErrorResourceUserExpiredSession ||
        rpcError instanceof ErrorJwtExpired
      ) {
        // remove session from storage
        storage.removeItem(
          authStore.__getStorageKeyForEnv(this.oppositeEnv).session,
        );
        storage.removeItem(
          currentUserStore.__getStorageKeyForEnv(this.oppositeEnv),
        );

        return;
      }

      throw rpcError;
    }

    const {session} = json.result!;

    // save session back to prevent logout
    const sessionToStore: StoredSession = {
      expires: Date.parse(datetimeObjToISOString(session.expires)),
      id: session._id,
      token: session.token,
    };

    storage.setItem(
      authStore.__getStorageKeyForEnv(this.oppositeEnv).session,
      JSON.stringify(sessionToStore),
    );

    return json.result!.access.token;
  }

  private async getSessionTokenForEnv(env: CoreOrB2bEnv) {
    const sessionString = await storage.getItem(
      authStore.__getStorageKeyForEnv(env).session,
    );

    let sessionToken = '';

    try {
      const session = JSON.parse(sessionString || '') as {
        token: string;
      };

      sessionToken = session?.token || '';
    } catch (error) {
      // do nothing
    }

    return sessionToken;
  }

  private async getUnreadEventsCount(
    env: CoreOrB2bEnv,
    accessToken: string,
    apiEndpoint: string,
    apiVersion: string,
  ) {
    const excludedEvents = getExcludedEvents(env).filter(
      event => event !== 'post_created',
    );

    const result = await fetch(apiEndpoint, {
      body: JSON.stringify({
        id: 2,
        meta: {
          access_token: accessToken,
          version: apiVersion.split('.').map(x => +x),
        },
        method: 'user.events.count',
        params: {
          query: [
            ['type', '!in', excludedEvents],
            ['read', '==', null],
          ],
        },
      }),
      method: 'POST',
    });

    const json = (await result.json()) as {
      error: Nullable<RpcError>;
      result: Nullable<{_count: number}>;
    };

    if (json.error) {
      throw parseRpcErrorFactory(json.error, {});
    }

    return json.result?._count || 0;
  }

  private isCoreUserHasAccessToB2b() {
    return isUserHasRole('coach')
      ? isUserHasRole(CORE_COACH_ACCESS_TO_B2B_ROLE)
      : envManager.get('appEnv') !== 'prod';
  }

  private onAppStateChange(state: AppStateManager.State) {
    switch (state) {
      case 'active': {
        // we need to save the current env to the storage
        // to open new tabs with the same env
        storage.setItem(STORAGE_KEY, this.env);

        this.startFetchingUpdates();
        break;
      }
      case 'background':
        this.stopFetchingUpdates();
        break;
      default:
        break;
    }
  }

  private async setEnv(env: CoreOrB2bEnv, shouldReloadApp = false) {
    await storage.setItem(STORAGE_KEY, env);

    if (shouldReloadApp) {
      reloadApp(platform.isWeb);
    } else {
      this.env = env;
    }
  }

  private setIsReady() {
    this.isReady = true;
  }

  private setupReactions() {
    this.disposer.watch(
      'ready',
      when(
        () => this.isReady && envManager.isReady,
        () => {
          this.startFetchingUpdates();
        },
      ),
    );
  }

  private startFetchingUpdates() {
    // means we already started fetching updates
    if (this.refreshUpdatesTimer !== null) {
      return;
    }

    if (this.isReady && envManager.isReady) {
      this.fetchUpdatesForEnv(this.env!);

      this.refreshUpdatesTimer = +setInterval(() => {
        this.fetchUpdatesForEnv(this.env!);
      }, REFRESH_UPDATES_COUNT_INTERVAL);
    }
  }

  private stopFetchingUpdates() {
    if (this.refreshUpdatesTimer !== null) {
      clearInterval(this.refreshUpdatesTimer);

      this.refreshUpdatesTimer = null;
    }
  }

  private async switchEnv(env: CoreOrB2bEnv) {
    // reset user to re-init it with the new env
    logger.setUser(null);

    this.updatesCount[env] = 0;

    this.stopFetchingUpdates();

    await this.setEnv(env, true);
  }

  private switchToB2b() {
    logger.event('switch_to_b2b_tap');

    this.switchEnv('b2b');
  }

  private switchToCore() {
    logger.event('switch_to_core_tap');

    this.switchEnv('core');
  }
}

const ENVS_TO_DISABLE_B2B_SWITCH = ['dev', 'stage1', 'stage2', 'rc', 'prod'];

class CoreOrB2bEnvSwitcherBridge {
  private _delegate:
    | CoreOrB2bEnvSwitcher
    | CoreOrB2bEnvSwitcherCoreOnly
    | null = null;

  constructor() {
    makeAutoObservable(this, undefined, {autoBind: true});
  }

  destruct() {
    this._delegate?.destruct();
  }

  get isB2b() {
    return this._delegate?.isB2b ?? false;
  }

  get isCore() {
    return this._delegate?.isCore ?? true;
  }

  get isOppositeEnvHasUpdates() {
    return this._delegate?.isOppositeEnvHasUpdates ?? false;
  }

  get isReady() {
    return this._delegate?.isReady ?? false;
  }

  get isSwitchAvailable() {
    return this._delegate?.isSwitchAvailable ?? false;
  }

  get oppositeEnv(): CoreOrB2bEnv {
    return this._delegate?.oppositeEnv ?? 'b2b';
  }

  get shouldShowButtonTemporary() {
    return this._delegate?.shouldShowButtonTemporary ?? false;
  }

  setup(appEnv: string) {
    if (this._delegate) {
      return;
    }

    if (ENVS_TO_DISABLE_B2B_SWITCH.includes(appEnv)) {
      this._delegate = new CoreOrB2bEnvSwitcherCoreOnly();
    } else {
      this._delegate = new CoreOrB2bEnvSwitcher();
    }
  }

  toggleEnv() {
    this._delegate?.toggleEnv();
  }
}

export const coreOrB2bEnvSwitcher = new CoreOrB2bEnvSwitcherBridge();
